import React, { useState, useCallback, useRef } from "react";
import Cropper from "react-easy-crop";
import { Area } from "react-easy-crop";
import { Upload, X, Crop, ImagePlus, ZoomIn, ZoomOut } from "lucide-react";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { t } from "../../locales";

const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous");
    image.src = url;
  });

async function getCroppedImg(
  imageSrc: string,
  pixelCrop: Area
): Promise<string> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("No 2d context");

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return canvas.toDataURL("image/jpeg", 0.85);
}

type Props = {
  value: string | null;
  onChange: (base64: string | null) => void;
  label?: string;
  existingImageUrl?: string | null;
};

const ImageCropUpload = ({
  value,
  onChange,
  label,
  existingImageUrl,
}: Props) => {
  const [rawImage, setRawImage] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [showCropDialog, setShowCropDialog] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL?.replace("/api", "");

  const displayImage =
    value ||
    (existingImageUrl ? `${apiBaseUrl}/uploads/${existingImageUrl}` : null);

  const onSelectFile = (file: File) => {
    if (!file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = () => {
      setRawImage(reader.result as string);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setShowCropDialog(true);
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) onSelectFile(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => setIsDragOver(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onSelectFile(file);
    if (e.target) e.target.value = "";
  };

  const onCropComplete = useCallback((_: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleCropDone = async () => {
    if (rawImage && croppedAreaPixels) {
      const croppedBase64 = await getCroppedImg(rawImage, croppedAreaPixels);
      onChange(croppedBase64);
      setShowCropDialog(false);
      setRawImage(null);
    }
  };

  const handleRemove = () => {
    onChange(null);
    setRawImage(null);
  };

  return (
    <div className="space-y-2">
      {label && (
        <label className="text-sm font-medium text-gray-700">{label}</label>
      )}

      {displayImage ? (
        <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <img
            src={displayImage}
            alt={t.imageUpload.imageAlt}
            className="w-20 h-20 object-cover rounded-lg border border-gray-200 flex-shrink-0"
          />
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center gap-1.5 text-sm text-[#113F67] hover:text-[#0d2f4f] font-medium"
            >
              <ImagePlus className="w-4 h-4" />
              {t.imageUpload.changeImage}
            </button>
            <button
              type="button"
              onClick={handleRemove}
              className="inline-flex items-center gap-1.5 text-sm text-red-500 hover:text-red-600 font-medium"
            >
              <X className="w-4 h-4" />
              {t.common.remove}
            </button>
          </div>
        </div>
      ) : (
        <div
          onClick={() => fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
            isDragOver
              ? "border-[#113F67] bg-blue-50"
              : "border-gray-300 hover:border-[#113F67] hover:bg-gray-50"
          }`}
        >
          <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
          <p className="text-sm text-gray-500">
            {t.imageUpload.dropOrBrowse}
          </p>
          <p className="text-xs text-gray-400 mt-1">{t.imageUpload.formatHint}</p>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />

      {/* Crop Dialog */}
      <Dialog
        open={showCropDialog}
        onOpenChange={(open) => {
          if (!open) {
            setShowCropDialog(false);
            setRawImage(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#113F67] flex items-center gap-2">
              <Crop className="w-5 h-5" /> {t.imageUpload.cropTitle}
            </DialogTitle>
            <DialogDescription>
              {t.imageUpload.cropDescription}
            </DialogDescription>
          </DialogHeader>

          <div className="relative w-full h-64 sm:h-80 bg-gray-900 rounded-lg overflow-hidden">
            {rawImage && (
              <Cropper
                image={rawImage}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            )}
          </div>

          {/* Zoom slider */}
          <div className="flex items-center gap-3 px-1">
            <ZoomOut className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <div className="flex-1 relative h-10 flex items-center">
              <div className="absolute inset-x-0 h-1.5 bg-gray-200 rounded-full" />
              <div
                className="absolute left-0 h-1.5 bg-[#113F67] rounded-full"
                style={{ width: `${((zoom - 1) / 2) * 100}%` }}
              />
              <input
                type="range"
                min={1}
                max={3}
                step={0.05}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="absolute inset-x-0 w-full h-10 opacity-0 cursor-pointer"
              />
              <div
                className="absolute w-5 h-5 bg-[#113F67] rounded-full shadow-md border-2 border-white pointer-events-none"
                style={{ left: `calc(${((zoom - 1) / 2) * 100}% - 10px)` }}
              />
            </div>
            <ZoomIn className="w-4 h-4 text-gray-400 flex-shrink-0" />
          </div>

          <div className="flex gap-3 justify-end border-t pt-3">
            <Button
              type="button"
              variant="outline"
              className="h-11 px-5 rounded-lg border-gray-300 text-[#113F67] hover:bg-gray-50 hover:text-[#113F67]"
              onClick={() => {
                setShowCropDialog(false);
                setRawImage(null);
              }}
            >
              {t.common.cancel}
            </Button>
            <Button
              type="button"
              className="h-11 px-5 rounded-lg bg-[#113F67] hover:bg-[#0d2f4f] border border-[#113F67]"
              onClick={handleCropDone}
            >
              {t.imageUpload.applyCrop}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ImageCropUpload;
